import React, { useState } from 'react';
import { LifetimeGift } from '../../../types';
import { X, CheckCircle2, AlertTriangle, AlertCircle, RotateCcw, Users } from 'lucide-react';
import {
    ParsedGiftRow, RecurringGroup,
    detectRecurringGroups, mapToLifetimeGift
} from './giftImportUtils';

interface Props {
    rows: ParsedGiftRow[];
    onConfirm: (gifts: LifetimeGift[]) => void;
    onClose: () => void;
}

const RECIPIENT_OPTIONS = [
    { value: 'other', label: 'Other Person' },
    { value: 'spouse', label: 'Spouse / CP' },
    { value: 'charity', label: 'Charity' },
] as const;

const StatusBadge: React.FC<{ status: ParsedGiftRow['status']; issues: string[] }> = ({ status, issues }) => {
    if (status === 'valid') return (
        <span className="flex items-center gap-1 text-ink2 text-xs font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> Valid
        </span>
    );
    if (status === 'warn') return (
        <span title={issues.join('; ')} className="flex items-center gap-1 text-[#7A5A00] text-xs font-medium cursor-help">
            <AlertTriangle className="w-3.5 h-3.5" /> {issues[0]}
        </span>
    );
    return (
        <span title={issues.join('; ')} className="flex items-center gap-1 text-ink2 text-xs font-medium cursor-help">
            <AlertCircle className="w-3.5 h-3.5" /> {issues[0]}
        </span>
    );
};

export const GiftImportModal: React.FC<Props> = ({ rows: initialRows, onConfirm, onClose }) => {
    const [rows, setRows] = useState<ParsedGiftRow[]>(initialRows);
    const [markedRegular, setMarkedRegular] = useState<Set<string>>(new Set());

    const recurringGroups: RecurringGroup[] = detectRecurringGroups(rows);

    const updateRow = (id: string, field: keyof ParsedGiftRow, value: any) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const updated = { ...r, [field]: value };
            // Re-validate so status reflects the current edited values
            const hasAmount = updated.amount !== null && !isNaN(updated.amount as number) && (updated.amount as number) > 0;
            const hasDate = !!updated.date;
            const issues: string[] = [];
            if (!hasDate) issues.push('Date unclear — please check format (DD/MM/YYYY)');
            if (!hasAmount) issues.push('Amount missing or invalid');
            const status: ParsedGiftRow['status'] = issues.length === 0 ? 'valid' : 'error';
            return { ...updated, status, issues };
        }));
    };

    const removeRow = (id: string) => {
        setRows(prev => prev.filter(r => r.id !== id));
    };

    const markGroupRegular = (group: RecurringGroup) => {
        setRows(prev => prev.map(r =>
            group.rowIds.includes(r.id) ? { ...r, is_regular_from_income: true } : r
        ));
        setMarkedRegular(prev => {
            const next = new Set(prev);
            group.rowIds.forEach(id => next.add(id));
            return next;
        });
    };

    const validCount = rows.filter(r => r.amount !== null && r.amount > 0 && r.date).length;
    const errorCount = rows.filter(r => r.status === 'error').length;

    const handleConfirm = () => {
        const importable = rows.filter(r => r.amount !== null && r.date);
        onConfirm(importable.map(mapToLifetimeGift));
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-rule">
                    <div>
                        <h2 className="text-lg font-bold text-ink">Review imported gifts</h2>
                        <p className="text-xs text-ink3 mt-0.5">
                            {rows.length} row{rows.length !== 1 ? 's' : ''} found —&nbsp;
                            <span className="text-ink2 font-medium">{validCount} ready to import</span>
                            {errorCount > 0 && <>, <span className="text-ink2 font-medium">{errorCount} need attention</span></>}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-ink3 hover:text-ink2 p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Recurring Gift Suggestions */}
                {recurringGroups.length > 0 && (
                    <div className="px-6 pt-4 space-y-2">
                        {recurringGroups.map(group => {
                            const alreadyMarked = group.rowIds.every(id => markedRegular.has(id));
                            return (
                                <div key={group.recipientName} className="flex items-center justify-between bg-accent-soft border border-accent rounded-lg px-4 py-2.5">
                                    <div className="flex items-start gap-2">
                                        <Users className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-xs font-semibold text-ink">
                                                {group.count} gifts detected to {group.recipientName}
                                                &nbsp;({group.totalAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })} total)
                                            </p>
                                            <p className="text-[10px] text-accent mt-0.5">
                                                These appear to be recurring gifts. Mark them as regular gifts from income if they came from surplus earnings?
                                            </p>
                                        </div>
                                    </div>
                                    {alreadyMarked ? (
                                        <span className="text-[10px] font-bold text-ink2 flex items-center gap-1 shrink-0 ml-4">
                                            <CheckCircle2 className="w-3 h-3" /> Marked
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => markGroupRegular(group)}
                                            className="ml-4 shrink-0 text-[10px] font-bold text-accent bg-white border border-accent rounded px-2 py-1 hover:bg-accent-soft transition-colors whitespace-nowrap"
                                        >
                                            Mark as regular
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Table */}
                <div className="overflow-auto flex-1 px-6 py-4">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-rule text-left">
                                <th className="pb-2 pr-3 font-bold text-ink3 uppercase tracking-wide">Date</th>
                                <th className="pb-2 pr-3 font-bold text-ink3 uppercase tracking-wide">Amount</th>
                                <th className="pb-2 pr-3 font-bold text-ink3 uppercase tracking-wide">Recipient</th>
                                <th className="pb-2 pr-3 font-bold text-ink3 uppercase tracking-wide">Relationship</th>
                                <th className="pb-2 pr-3 font-bold text-ink3 uppercase tracking-wide">Regular?</th>
                                <th className="pb-2 font-bold text-ink3 uppercase tracking-wide">Status</th>
                                <th className="pb-2"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-rule-soft">
                            {rows.map(row => (
                                <tr key={row.id} className={`${row.status === 'error' ? 'bg-paper/40' : row.status === 'warn' ? 'bg-[#FAF3E0]/30' : ''}`}>
                                    {/* Date */}
                                    <td className="py-2 pr-3">
                                        <input
                                            type="month"
                                            className={`border rounded px-2 py-1 text-xs w-32 ${!row.date ? 'border-rule bg-paper' : 'border-rule'}`}
                                            value={row.date}
                                            onChange={e => updateRow(row.id, 'date', e.target.value)}
                                        />
                                    </td>
                                    {/* Amount */}
                                    <td className="py-2 pr-3">
                                        <div className="relative">
                                            <span className="absolute left-2 top-1.5 text-ink3">£</span>
                                            <input
                                                type="number"
                                                className={`border rounded pl-5 pr-2 py-1 text-xs w-28 ${!row.amount ? 'border-rule bg-paper' : 'border-rule'}`}
                                                value={row.amount ?? ''}
                                                onChange={e => updateRow(row.id, 'amount', parseFloat(e.target.value) || null)}
                                            />
                                        </div>
                                    </td>
                                    {/* Recipient name */}
                                    <td className="py-2 pr-3">
                                        <input
                                            type="text"
                                            className="border border-rule rounded px-2 py-1 text-xs w-32"
                                            value={row.recipientName}
                                            onChange={e => updateRow(row.id, 'recipientName', e.target.value)}
                                            placeholder="Name"
                                        />
                                    </td>
                                    {/* Recipient type */}
                                    <td className="py-2 pr-3">
                                        <select
                                            className="border border-rule rounded px-2 py-1 text-xs w-28"
                                            value={row.recipient_type}
                                            onChange={e => updateRow(row.id, 'recipient_type', e.target.value as any)}
                                        >
                                            {RECIPIENT_OPTIONS.map(o => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    {/* Regular */}
                                    <td className="py-2 pr-3 text-center">
                                        <input
                                            type="checkbox"
                                            className="accent-blue-600"
                                            checked={row.is_regular_from_income}
                                            onChange={e => updateRow(row.id, 'is_regular_from_income', e.target.checked)}
                                        />
                                    </td>
                                    {/* Status */}
                                    <td className="py-2 pr-2">
                                        <StatusBadge status={row.status} issues={row.issues} />
                                    </td>
                                    {/* Remove */}
                                    <td className="py-2">
                                        <button
                                            onClick={() => removeRow(row.id)}
                                            className="text-sub hover:text-ink2 transition-colors p-0.5"
                                            title="Remove this row"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {rows.length === 0 && (
                        <div className="text-center py-8 text-ink3 text-sm">
                            All rows removed. Close and try uploading again.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-rule flex items-center justify-between">
                    <button onClick={onClose} className="text-sm text-ink3 hover:text-ink2 flex items-center gap-1">
                        <RotateCcw className="w-3.5 h-3.5" /> Start over
                    </button>
                    <div className="flex items-center gap-3">
                        {errorCount > 0 && (
                            <p className="text-xs text-[#7A5A00]">
                                {errorCount} row{errorCount !== 1 ? 's' : ''} with errors will be skipped
                            </p>
                        )}
                        <button
                            onClick={handleConfirm}
                            disabled={validCount === 0}
                            className="bg-accent text-white text-sm font-bold px-5 py-2 rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Import {validCount} gift{validCount !== 1 ? 's' : ''}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
